import React, { useEffect, useId, useRef } from 'react'
import { Flex, Input, InputNumber, Select, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'
import {
    FieldOptionsFooter,
    scrolledToEnd,
    useFieldOptions,
    type ValueInputComponent,
    type ValueInputProps,
} from '@pushly/aqe'
import { Select as AqeSelect } from '@pushly/aqe/lib/components/select/select'
import { PageTagSelect } from './page-tag-select'
import { KEYWORD_VALUE_FIELD } from './field-value-loader'

/**
 * How the expression half of the pair is entered.
 *
 * `none` is a positive statement that the surface has no expression: the condition
 * counts every event of its kind, and the wire node carries no `meta.expression`.
 * Every other arm names both the control and the wording that introduces it, so a
 * surface is fully described by its configuration.
 */
/**
 * What narrows a count, and how it is named in each of the two places it appears.
 *
 * `label` names the control in the editor, where it sits above its input. `summaryLead` introduces
 * the same value mid-sentence on a collapsed row. They are separate because one is a form label and
 * the other is a phrase, and deriving the phrase from the label restated the field's own noun:
 * "Page / Screen Tag Frequency ... where page tag zero-margin". A surface whose label happens to read
 * as a predicate can set them to the same words.
 */
export type CountInWindowExpression =
    | { kind: 'none' }
    | { kind: 'freeText'; label: string; summaryLead: string }
    | { kind: 'keyword'; label: string; placeholder: string; summaryLead: string }
    | { kind: 'pageTag'; label: string; placeholder: string; summaryLead: string }

export interface CountInWindowWindowOption {
    label: string
    value: string
}

/**
 * Everything that varies between the surfaces that count events in a window.
 *
 * The shape they share is fixed by the engine: the count is the condition's value,
 * the window is `meta.scope`, and whatever narrows the count is `meta.expression`.
 * Only the wording, the bounds and the window vocabulary differ, so those are
 * configuration rather than nine components.
 */
export interface CountInWindowConfig {
    /**
     * The windows offered, in the order the reader sees them. The first is what a
     * row with no stored window starts on, so the order is part of the contract.
     */
    windowOptions: CountInWindowWindowOption[]
    /** The count a row starts on when the stored condition carries none. */
    defaultCount: number
    /** Upper bound on the count. */
    maxValue: number
    /** Units shown after the count. `null` suppresses the units entirely. */
    valueLabel: string | null
    /** Shown inside the count box ahead of the number — a currency symbol, say. */
    valuePrefix?: string
    /**
     * Lead-in text for the count box, where the surface has one.
     *
     * Omitted where the reader is given no such phrase; the box then takes the
     * field's own name, so it is never an unlabelled number.
     */
    countLabel?: string
    expression: CountInWindowExpression
    /** Qualifies what the window does and does not include. */
    tooltip?: string
}

/**
 * Label for the window select. Every surface names its window the same way, and so does the
 * summary a collapsed row reads a stored window back as.
 */
export const WINDOW_LABEL = 'Time window'

export interface CountInWindowInputProps extends ValueInputProps<number | null> {
    config: CountInWindowConfig
    /**
     * Whether the expression is being rendered above the operator by {@link CountInWindowExpression}
     * instead of here.
     *
     * Default false, so a surface that renders this component directly still shows its expression.
     * Only {@link createCountInWindowInput} sets it, because only that path also registers the
     * component that takes the expression over.
     */
    expressionHoisted?: boolean
}

/**
 * One count-in-a-window surface: a count, the window it is counted over, and
 * whatever narrows what is counted.
 *
 * The condition's operator is deliberately absent: the builder renders the row's own
 * operator control above this one and gives a value input no way to author it, so a
 * copy here could only ever disagree with the control the reader actually edits.
 */
export function CountInWindowInput({
    config,
    value,
    onChange,
    field,
    meta,
    onMetaChange,
    disabled,
    size,
    getPopupContainer,
    expressionHoisted = false,
}: CountInWindowInputProps) {
    const idBase = useId()
    const countId = `${idBase}-count`
    const windowId = `${idBase}-window`

    const expression = meta?.expression ?? ''
    const defaultScope = config.windowOptions[0].value

    // The catalog states how finely a field's value divides. A count of events
    // does not divide at all, which is what the absent declaration means, so a
    // whole number is the floor rather than a restriction imposed here.
    const precision = field.precision ?? 0

    // ─── The window the row counts over ──────────────────────────────────────
    //
    // The engine reads an absent `meta.scope` as no window at all — the count is
    // taken over the subscriber's whole history — so a row showing a period while
    // the condition names none matches a different audience from the one on
    // screen. The window on screen is therefore derived and always defined: the
    // stored one wherever there is one, and this surface's first option wherever
    // there is not. The effect below writes the same answer through to the
    // condition, so what the reader sees is what the segment is saved with.
    //
    // A stored window this surface does not offer is still shown as it stands.
    // The engine can read it, and replacing it with one this surface prefers
    // would change the audience of a condition nobody asked to change.
    const storedScope = meta?.scope ?? null
    const scope = storedScope ?? defaultScope

    const latest = useRef({ onChange, onMetaChange })
    latest.current = { onChange, onMetaChange }

    /**
     * The field the count has already been offered a starting value for.
     *
     * One `inputKey` serves several fields, so re-pointing a row from one of these
     * surfaces to another re-renders this component rather than remounting it, and
     * the editor clears the condition's value on that change. The offer is keyed on
     * the field rather than made once, so the row arrives on its new surface holding
     * that surface's own starting count instead of an empty box.
     */
    const countOfferedFor = useRef<string | null>(null)

    useEffect(() => {
        // A row nobody can edit states nothing on the reader's behalf.
        if (disabled) return

        // At most one write per pass. Both writes clone the single draft condition
        // the editor holds, so a pair issued in the same tick would clone the same
        // stale draft and the second would drop the first. A write re-renders this
        // component with the draft it produced, which runs this again for whatever
        // is still outstanding.
        //
        // The window goes first because it is the half nothing else would catch: a
        // count that never lands is reported by the validator and blocks the save,
        // where a window that never lands would be saved and silently widen the
        // audience.
        if (storedScope === null) {
            latest.current.onMetaChange?.({ scope: defaultScope })
            return
        }

        if (countOfferedFor.current !== field.key) {
            countOfferedFor.current = field.key

            // Only an absent count is filled in. A count the reader cleared stays
            // cleared: re-filling it would fight them for the box and hide the gap
            // the validator is there to report.
            if (value == null) latest.current.onChange(config.defaultCount)
        }
    }, [disabled, field.key, value, storedScope, defaultScope, config.defaultCount])

    return (
        <Flex className="segment-count-in-window" vertical gap="small">
            {!expressionHoisted && (
                <CountInWindowExpression
                    config={config}
                    field={field}
                    meta={meta}
                    onMetaChange={onMetaChange}
                    disabled={disabled}
                    size={size}
                    getPopupContainer={getPopupContainer}
                />
            )}

            <Flex className="segment-count-in-window__field" vertical gap={4}>
                {config.countLabel !== undefined && <label htmlFor={countId}>{config.countLabel}</label>}
                <Flex gap="small" align="center">
                    <InputNumber
                        id={countId}
                        // A surface with no lead-in phrase would otherwise leave the reader
                        // a bare number box; the field's own name is what the row is asking
                        // for a count of.
                        aria-label={config.countLabel === undefined ? field.label : undefined}
                        size={size}
                        disabled={disabled}
                        min={0}
                        max={config.maxValue}
                        precision={precision}
                        prefix={config.valuePrefix}
                        value={value ?? null}
                        onChange={(next) => onChange(next)}
                    />
                    {config.valueLabel !== null && <span>{config.valueLabel}</span>}
                </Flex>
            </Flex>

            <Flex className="segment-count-in-window__field" vertical gap={4}>
                {/*
                 * The note qualifies the window rather than the whole condition, so it sits with
                 * the window's own label. As a sibling of the label — never inside it — the
                 * select's accessible name stays the label's words alone.
                 */}
                <Flex gap={4} align="center">
                    <label htmlFor={windowId}>{WINDOW_LABEL}</label>
                    {config.tooltip !== undefined && (
                        <Tooltip title={config.tooltip}>
                            <span aria-label={config.tooltip}>
                                <InfoCircleOutlined aria-hidden="true" />
                            </span>
                        </Tooltip>
                    )}
                </Flex>
                <Select
                    id={windowId}
                    size={size}
                    disabled={disabled}
                    value={scope}
                    options={config.windowOptions}
                    onChange={(next: string) => onMetaChange?.({ scope: next })}
                    getPopupContainer={getPopupContainer}
                />
            </Flex>
        </Flex>
    )
}

/**
 * What the count is taken over — the keyword clicked, the page tag visited, the URL matched.
 *
 * Its own component because it is rendered in its own row, above the operator: the operator
 * qualifies the count alone, so being asked to choose "is at least" before having said what is being
 * counted reads backwards. See `ValueInputComponent.BeforeOperator` in aqe.
 *
 * Props are a subset of the value input's, which is what lets the same component be handed the full
 * set by the builder and a narrower set by {@link CountInWindowInput}.
 */
export function CountInWindowExpression({
    config,
    field,
    meta,
    onMetaChange,
    disabled,
    size,
    getPopupContainer,
}: {
    config: CountInWindowConfig
    field: PQueryBuilderField
    meta?: ValueInputProps<number | null>['meta']
    onMetaChange?: ValueInputProps<number | null>['onMetaChange']
    disabled?: boolean
    size?: 'small' | 'middle' | 'large'
    getPopupContainer?: () => HTMLElement
}) {
    const expressionId = `${useId()}-expression`
    const expression = meta?.expression ?? ''
    const setExpression = (next: string) => onMetaChange?.({ expression: next })

    if (config.expression.kind === 'none') return null

    return (
        <Flex className="segment-count-in-window__field" vertical gap={4}>
            <label htmlFor={expressionId}>{config.expression.label}</label>
            {config.expression.kind === 'freeText' && (
                <Input
                    id={expressionId}
                    size={size}
                    disabled={disabled}
                    value={expression}
                    onChange={(event) => setExpression(event.target.value)}
                />
            )}
            {config.expression.kind === 'keyword' && (
                <KeywordSelect
                    id={expressionId}
                    placeholder={config.expression.placeholder}
                    size={size}
                    disabled={disabled}
                    value={expression}
                    onChange={setExpression}
                    getPopupContainer={getPopupContainer}
                />
            )}
            {config.expression.kind === 'pageTag' && (
                <PageTagSelect
                    id={expressionId}
                    placeholder={config.expression.placeholder}
                    property={field.key}
                    size={size}
                    disabled={disabled}
                    value={expression}
                    onChange={setExpression}
                    getPopupContainer={getPopupContainer}
                />
            )}
        </Flex>
    )
}

interface ExpressionSelectProps {
    id: string
    placeholder: string
    value: string
    onChange: (next: string) => void
    disabled?: boolean
    size?: 'small' | 'middle' | 'large'
    getPopupContainer?: () => HTMLElement
}

/**
 * The keyword a condition counts clicks on.
 *
 * Read through the same reader as the Keyword condition's own value list, so the two offer the same
 * keywords a page at a time and search them the same way. They are the same vocabulary, and a reader
 * who has learned how one of them behaves should not have to learn the other: a second list loaded
 * whole and filtered in the browser looked the same and answered differently, which is worse than
 * either behaviour on its own.
 *
 * A stored keyword that the list does not contain is still offered: a condition saved against a
 * keyword since renamed or retired has to stay legible, and dropping it would blank the row and
 * silently rewrite the segment on the next save.
 */
function KeywordSelect({ id, placeholder, value, onChange, disabled, size, getPopupContainer }: ExpressionSelectProps) {
    const selected = React.useMemo(() => (value ? [value] : []), [value])
    const { options, loading, remote, hasMore, onSearch, loadMore } = useFieldOptions(KEYWORD_VALUE_FIELD, selected)
    const [searched, setSearched] = React.useState(false)

    return (
        <AqeSelect
            id={id}
            // A list the source has already narrowed must not be narrowed again here: filtering it
            // a second time would hide keywords the source chose to return.
            showSearch={
                remote
                    ? {
                          filterOption: false,
                          onSearch: (search: string) => {
                              setSearched(!!search)
                              onSearch?.(search)
                          },
                      }
                    : true
            }
            onPopupScroll={remote ? (event) => scrolledToEnd(event) && loadMore?.() : undefined}
            popupRender={
                remote
                    ? (menu) => (
                          <>
                              {menu}
                              <FieldOptionsFooter
                                  paged={remote}
                                  loading={loading}
                                  hasMore={hasMore}
                                  searched={searched}
                              />
                          </>
                      )
                    : undefined
            }
            placeholder={placeholder}
            loading={loading}
            size={size}
            disabled={disabled}
            value={value === '' ? undefined : value}
            options={options}
            onChange={onChange}
            getPopupContainer={getPopupContainer}
        />
    )
}

/**
 * Builds the value input for one surface whose configuration never varies.
 *
 * A surface that has to read something from its surroundings — a currency, say —
 * renders {@link CountInWindowInput} directly with a configuration it computes.
 */
export function createCountInWindowInput(config: CountInWindowConfig): ValueInputComponent<number | null> {
    const hoistExpression = config.expression.kind !== 'none'

    function BoundCountInWindowInput(props: ValueInputProps<number | null>) {
        return <CountInWindowInput {...props} config={config} expressionHoisted={hoistExpression} />
    }

    BoundCountInWindowInput.displayName = 'CountInWindowInput'

    // Built per config and attached to this closure alone. Assigning onto a shared component would
    // give the slot to every surface, expression or not.
    const bound: ValueInputComponent<number | null> = BoundCountInWindowInput

    if (hoistExpression) {
        const BoundCountInWindowExpression = (props: ValueInputProps<number | null>) => (
            <CountInWindowExpression {...props} config={config} />
        )

        BoundCountInWindowExpression.displayName = 'CountInWindowExpression'
        bound.BeforeOperator = BoundCountInWindowExpression
    }

    return bound
}
